/**
 * @wharfkit/transact-plugin-mock v1.0.0
 * https://github.com/wharfkit/transact-plugin-mock
 *
 * @license
 * Copyright (c) 2021 FFF00 Agents AB & Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { AbstractTransactPlugin, TransactHookTypes, Canceled } from '@wharfkit/session';

var title$3 = "Example Prompt!";
var body$3 = "An example prompt from the TransactPluginMock for testing purposes.";
var timeout$3 = "This prompt will automatically cancel in {{timeout}} seconds.";
var en = {
	title: title$3,
	body: body$3,
	timeout: timeout$3,
	"timeout-trigger": "Test prompt timed out automatically after {{timeout}} seconds."
};

var title$2 = "예제 프롬프트!";
var body$2 = "테스트 목적으로 TransactPluginMock에서 프롬프트 예제입니다.";
var timeout$2 = "이 프롬프트는 {{timeout}} 초 후에 자동으로 취소됩니다.";
var ko = {
	title: title$2,
	body: body$2,
	timeout: timeout$2,
	"timeout-trigger": "{{timeout}}초 후에 테스트 프롬프트가 자동으로 시간 초과되었습니다."
};

var title$1 = "示例！";
var body$1 = "来自 TransactPluginMock 的提示窗口，用于测试目的。";
var timeout$1 = "此提示将在 {{timeout}} 秒内自动取消。";
var zh_hans = {
	title: title$1,
	body: body$1,
	timeout: timeout$1,
	"timeout-trigger": "测试提示在 {{timeout}} 秒后自动超时。"
};

var title = "示例！";
var body = "來自 TransactPluginMock 的提示窗口，用於測試目的。";
var timeout = "此提示將在 {{timeout}} 秒內自動取消。";
var zh_hant = {
	title: title,
	body: body,
	timeout: timeout,
	"timeout-trigger": "測試提示在 {{timeout}} 秒後自動超時。"
};

var defaultTranslations = {
    en,
    ko,
    'zh-Hans': zh_hans,
    'zh-Hant': zh_hant,
};

class TransactPluginMock extends AbstractTransactPlugin {
    constructor(options) {
        super();
        /** Define any translations for this plugin */
        this.translations = defaultTranslations;
        /** Set a unique ID for the plugin */
        this.id = 'transact-plugin-mock';
        this.options = options;
    }
    /** Register any hooks required for the plugin to operate */
    register(context) {
        context.addHook(TransactHookTypes.beforeSign, async (request, context) => {
            if (this.options?.prompt && context.ui) {
                // Retrieve translation helper from the UI, passing the app ID
                const t = context.ui.getTranslate(this.id);
                // Customize the body to present the developer with information about this prompt
                let body = t('body', {
                    default: 'An example prompt from the TransactPluginMock for testing purposes.',
                });
                if (this.options.promptOptions.timeout) {
                    body +=
                        ' ' +
                            t('timeout', {
                                default: `This prompt will automatically cancel in {{timeout}} seconds.`,
                                timeout: this.options.promptOptions.timeout / 1000,
                            });
                }
                // Initiate a new cancelable prompt to inform the user of the fee required
                console.log('TransactPluginMock called context.ui.prompt().');
                const prompt = context.ui.prompt({
                    title: t('title', { default: 'Example Prompt!' }),
                    body,
                    elements: [
                        {
                            type: 'accept',
                        },
                    ],
                });
                // Create a timer to test the external cancelation of the prompt, if defined
                let timer;
                if (this.options.promptOptions.timeout) {
                    const { timeout } = this.options.promptOptions;
                    timer = setTimeout(() => {
                        console.log('TransactPluginMock setTimeout has executed.');
                        if (!context.ui) {
                            throw new Error('No UI defined');
                        }
                        prompt.cancel(t('timeout-trigger', {
                            default: `Test prompt timed out automatically after {{timeout}} seconds.`,
                            timeout: timeout / 1000,
                        }));
                    }, timeout);
                }
                // Return the promise from the prompt
                return prompt
                    .then(async () => {
                    // If the prompt was accepted, return the request
                    console.log('Prompt was accepted and returned to TransactPluginMock.');
                    return;
                })
                    .catch((e) => {
                    // Throw if what we caught was a cancelation of the prompt to abort
                    if (e instanceof Canceled) {
                        console.log('Prompt was cancelled and TransactPluginMock threw error.');
                        throw e;
                    }
                    // Determine if we should continue if the prompt was rejected, defaults to true.
                    let continueOnDecline = true;
                    if (this.options &&
                        this.options.promptOptions &&
                        this.options.promptOptions.continueOnDecline !== undefined) {
                        continueOnDecline = this.options.promptOptions.continueOnDecline;
                    }
                    if (continueOnDecline) {
                        // If the prompt was rejected, and we're configured to continue, return the request
                        console.log('Prompt was rejected and returned to TransactPluginMock.');
                        // Otherwise if it wasn't a cancel, it was a reject, and continue without modification
                        console.log('returning void');
                        return;
                    }
                    else {
                        // Otherwise if it was rejected and we shouldn't continue, and throw an error
                        console.log('Prompt was rejected and threw an error.');
                        throw e;
                    }
                })
                    .finally(() => {
                    // Always cleanup the timer
                    if (timer) {
                        console.log('Prompt timer was cleared.');
                        clearTimeout(timer);
                    }
                });
            }
        });
    }
}

export { TransactPluginMock };
//# sourceMappingURL=transact-plugin-mock.m.js.map
